import { Loading, useLocale, useTranslate } from 'react-admin';
import { Section } from '~/layout';
import { H2, H3, TextField } from '~/components';
import React, { FC } from 'react';
import Grid from '@material-ui/core/Grid';
import ConfigurationEdit from '~/resources/ledgers/mambu/components/ConfigurationEdit';
import { makeStyles } from '@material-ui/core';
import { l } from '~/resources/ledgers/mambu/components/ConfigurationForm';
import Table from '@material-ui/core/Table';
import cx from 'classnames';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '../../../../components/TableCell';
import TableBody from '@material-ui/core/TableBody';
import ReferenceField from '../../../../components/ReferenceField';
import { useGetMambuLedgerConfiguration } from '~/hooks/ledgers';
import i18n from '~/i18n';

interface ConfigurationTabProps {
  resource: string;
}

const ConfigurationTab: FC<ConfigurationTabProps> = (props) => {
  const { resource } = props;

  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));
  const td = (key: string): string => translate(`utils.${key}`);

  const { data, isLoading, refetch } = useGetMambuLedgerConfiguration();

  const classes = useStyles();

  const isExternalAccountLinkRegistrationEnabled =
    data?.externalAccountLinkRegistration?.enabled ?? false;
  const isAccountMirroringEnabled = data?.accountMirroring?.enabled ?? false;
  const isExternalProfileLinkSyncEnabled = data?.externalProfileLinkSync?.enabled ?? false;
  const isTransactionTransferMirroringEnabled =
    data?.transactionTransferMirroring?.enabled ?? false;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Grid container justify='space-between'>
      <Grid item xs={12} className={classes.headerSection}>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={0} bottomSpacing={2}>
              {t('apiConfiguration')}
            </H2>
          </Grid>
          <Grid item className='sectionActions'>
            <Grid container item spacing={2}>
              <Grid item>
                <ConfigurationEdit resource={resource} record={data} onOpenDrawer={refetch} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('baseUrl')}>{data?.api?.baseUrl}</TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField label={t('apiKey')}>{data?.api?.apiKey}</TextField>
            </Grid>
          </Grid>
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('basicConfiguration')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('timeZoneOffset')}>{data?.timeZoneOffset}</TextField>
            </Grid>
            <Grid item xs={4} />
          </Grid>
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('externalAccountLinkRegistration')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('isEnabled')}>
                {isExternalAccountLinkRegistrationEnabled ? td('yes') : td('no')}
              </TextField>
            </Grid>
            {isExternalAccountLinkRegistrationEnabled && (
              <Grid item xs={4}>
                <TextField label={t('regularIterationMs')}>
                  {data?.externalAccountLinkRegistration?.regularIterationMs}
                </TextField>
              </Grid>
            )}
            {isExternalAccountLinkRegistrationEnabled && (
              <Grid item xs={4}>
                <TextField label={t('initialIterationMs')}>
                  {data?.externalAccountLinkRegistration?.initialIterationMs}
                </TextField>
              </Grid>
            )}
          </Grid>
          {isExternalAccountLinkRegistrationEnabled && (
            <Grid container spacing={2} direction='row'>
              <Grid item xs={4}>
                <TextField label={t('batchSize')}>
                  {data?.externalAccountLinkRegistration?.batchSize}
                </TextField>
              </Grid>
            </Grid>
          )}
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('accountMirroring')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('isEnabled')}>
                {isAccountMirroringEnabled ? td('yes') : td('no')}
              </TextField>
            </Grid>
            {isAccountMirroringEnabled && (
              <Grid item xs={4}>
                <TextField label={t('regularIterationMs')}>
                  {data?.accountMirroring?.regularIterationMs}
                </TextField>
              </Grid>
            )}
          </Grid>
          {isAccountMirroringEnabled && (
            <Grid container spacing={2} direction='row'>
              <Grid item xs={4}>
                <TextField label={t('batchSize')}>{data?.accountMirroring?.batchSize}</TextField>
              </Grid>
            </Grid>
          )}
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('externalProfileLinkSync')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('isEnabled')}>
                {isExternalProfileLinkSyncEnabled ? td('yes') : td('no')}
              </TextField>
            </Grid>
            {isExternalProfileLinkSyncEnabled && (
              <Grid item xs={4}>
                <TextField label={t('regularIterationMs')}>
                  {data?.externalProfileLinkSync?.regularIterationMs}
                </TextField>
              </Grid>
            )}
          </Grid>
          {isExternalProfileLinkSyncEnabled && (
            <Grid container spacing={2} direction='row'>
              <Grid item xs={4}>
                <TextField label={t('batchSize')}>
                  {data?.externalProfileLinkSync?.batchSize}
                </TextField>
              </Grid>
            </Grid>
          )}
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('transactionTransferMirroring')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('isEnabled')}>
                {isTransactionTransferMirroringEnabled ? td('yes') : td('no')}
              </TextField>
            </Grid>
            {isTransactionTransferMirroringEnabled && (
              <Grid item xs={4}>
                <TextField label={t('regularIterationMs')}>
                  {data?.transactionTransferMirroring?.regularIterationMs}
                </TextField>
              </Grid>
            )}
            {isTransactionTransferMirroringEnabled && (
              <Grid item xs={4}>
                <TextField label={t('delaySeconds')}>
                  {data?.transactionTransferMirroring?.delaySeconds}
                </TextField>
              </Grid>
            )}
          </Grid>
          {isTransactionTransferMirroringEnabled && (
            <Grid container spacing={2} direction='row'>
              <Grid item xs={4}>
                <TextField label={t('batchSize')}>
                  {data?.transactionTransferMirroring?.batchSize}
                </TextField>
              </Grid>
            </Grid>
          )}
        </Section>
        {isTransactionTransferMirroringEnabled && (
          <>
            <Grid container justify='space-between' alignItems='flex-start'>
              <Grid item>
                <H3 topSpacing={2} bottomSpacing={2}>
                  {t('transitChannelKeys')}
                </H3>
              </Grid>
            </Grid>
            <Section>
              <TransitTable
                tableType='transactionTransferMirroring.transitChannelKeys'
                data={data?.transactionTransferMirroring.transitChannelKeys}
              />
            </Section>
            <Grid container justify='space-between' alignItems='flex-start'>
              <Grid item>
                <H3 topSpacing={2} bottomSpacing={2}>
                  {t('transitDepositKeys')}
                </H3>
              </Grid>
            </Grid>
            <Section>
              <TransitTable
                tableType='transactionTransferMirroring.transitDepositKeys'
                data={data?.transactionTransferMirroring.transitDepositKeys}
              />
            </Section>
          </>
        )}

        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('clientRoleKeys')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <MappingTable tableType='clientRoleKeys' data={data?.clientRoleKeys} />
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('groupRoleKeys')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <MappingTable tableType='groupRoleKeys' data={data?.groupRoleKeys} />
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('productTypeKeys')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <MappingTable tableType='productTypeKeys' data={data?.productTypeKeys} />
        </Section>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <H2 topSpacing={2} bottomSpacing={2}>
              {t('additionalInformation')}
            </H2>
          </Grid>
        </Grid>
        <Section>
          <Grid container spacing={2} direction='row'>
            <Grid item xs={4}>
              <TextField label={t('clientIdPrefix')}>{data?.clientIdPrefix}</TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField label={t('groupIdPrefix')}>{data?.groupIdPrefix}</TextField>
            </Grid>
          </Grid>
        </Section>
      </Grid>
    </Grid>
  );
};

interface MappingTableProps {
  tableType: 'groupRoleKeys' | 'clientRoleKeys' | 'productTypeKeys';
  data?: Record<string, string>;
}

const MappingTable: FC<MappingTableProps> = (props) => {
  const { tableType, data } = props;
  const classes = useStyles();

  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));

  const tableHeaders =
    tableType === 'groupRoleKeys'
      ? [t('veenguBusinessSolution'), t('veenguBusinessSolutionId'), t('mambuGroupRoleId')]
      : tableType === 'clientRoleKeys'
      ? [t('veenguIndividualSolution'), t('veenguIndividualSolutionId'), t('mambuClientRoleId')]
      : tableType === 'productTypeKeys'
      ? [t('veenguProduct'), t('veenguProductId'), t('mambuProductTypeId')]
      : [];
  const veenguObjectType =
    tableType === 'groupRoleKeys'
      ? 'SOLUTION'
      : tableType === 'clientRoleKeys'
      ? 'SOLUTION'
      : tableType === 'productTypeKeys'
      ? 'PRODUCT'
      : '';

  return (
    <Table className={cx(classes.table, { [classes.autoWidth]: true })}>
      <TableHead>
        <TableRow>
          <TableCell>{tableHeaders[0]}</TableCell>
          <TableCell>{tableHeaders[1]}</TableCell>
          <TableCell>{tableHeaders[2]}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(data ?? {}).map(([veenguId, mambuId], i) => (
          <TableRow key={i}>
            <TableCell>
              <ReferenceField record={{ id: veenguId, type: veenguObjectType }} />
            </TableCell>
            <TableCell>
              <TextField style={{ fontWeight: 400 }}>{veenguId}</TextField>
            </TableCell>
            <TableCell>
              <TextField style={{ fontWeight: 400 }}>{mambuId}</TextField>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface TransitTableProps {
  tableType:
    | 'transactionTransferMirroring.transitChannelKeys'
    | 'transactionTransferMirroring.transitDepositKeys';
  data?: Record<string, string>;
}

const TransitTable: FC<TransitTableProps> = (props) => {
  const { tableType, data } = props;
  const classes = useStyles();

  const locale: keyof any = useLocale();
  const allCurrencies: { [key: string]: string } = (i18n as any)[locale].currencies;

  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));

  const tableHeaders =
    tableType === 'transactionTransferMirroring.transitChannelKeys'
      ? [t('currency'), t('currencyCode'), t('mambuTransitChannel')]
      : tableType === 'transactionTransferMirroring.transitDepositKeys'
      ? [t('currency'), t('currencyCode'), t('mambuTransitDeposit')]
      : [];

  return (
    <Table className={cx(classes.table, { [classes.autoWidth]: true })}>
      <TableHead>
        <TableRow>
          <TableCell>{tableHeaders[0]}</TableCell>
          <TableCell>{tableHeaders[1]}</TableCell>
          <TableCell>{tableHeaders[2]}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(data ?? {}).map(([currencyCode, mambuId], i) => (
          <TableRow key={i}>
            <TableCell>
              <TextField>{allCurrencies[currencyCode]}</TextField>
            </TableCell>
            <TableCell>
              <TextField style={{ fontWeight: 400 }}>{currencyCode}</TextField>
            </TableCell>
            <TableCell>
              <TextField style={{ fontWeight: 400 }}>{mambuId}</TextField>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const useStyles = makeStyles((theme) => ({
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
  table: {
    width: '100%',
    '& > tbody': {
      '& > tr': {
        '& > td:first-child': {
          width: '33%',
        },
        '& > td:nth-child(2)': {
          width: '33%',
        },
        '& > td:nth-child(3)': {
          width: '34%',
        },
      },
    },
  },
  autoWidth: {},
}));

export default ConfigurationTab;
